import { Box } from '../../Box';
import { useInterstateTheme } from '../../InterstateThemeProvider';
import { ComboBoxInput } from './ComboBoxInput';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { FilterChip } from '../../Chip';
import { keyGen } from '../../internal/utils/keyGen';
import { Badge } from '../../Badge';
import { nanoid } from 'nanoid';
import FitWithinWidth from './FitWithinWidth';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
export const resolveChipPadding = size => {
  const map = {
    small: '4px',
    sm: '4px',
    medium: '8px',
    md: '8px',
    large: '12px',
    lg: '12px'
  };
  return map[size || 'medium'];
};
const resolveStyle = (theme, maxHeight, size) => ({
  display: 'flex',
  padding: `${resolveChipPadding(size)} 0px`,
  alignItems: 'center',
  alignContent: 'center',
  gap: '4px',
  flex: '1 0 0',
  alignSelf: 'stretch',
  flexWrap: 'wrap',
  maxHeight: maxHeight ? maxHeight : 'none',
  overflowY: maxHeight ? 'auto' : 'hidden'
});
const buildOptionChips = (options, onChipClick, disabled, selectionLimit, collapse) => {
  if (!options) {
    return null;
  }
  const chipArray = collapse ? options.slice(0, selectionLimit) : options.slice(0);
  const overFlow = options.length - chipArray.length;
  const chips = chipArray.map(option => {
    return /*#__PURE__*/_jsx(FilterChip, {
      label: option.label,
      id: option.id,
      onClick: onChipClick,
      value: option.value,
      "data-testid": option['data-testid'],
      tabIndex: -1,
      disabled: disabled
    }, keyGen());
  });
  if (overFlow) {
    chips.push( /*#__PURE__*/_jsx(Badge, {
      children: `+${overFlow}`
    }, nanoid()));
  }
  return chips;
};
export const ComboBoxMultiSelect = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    onChipClick,
    disabled,
    size,
    selectedOptions,
    placeholder,
    maxHeight,
    onInputFocus,
    onInputChange,
    onInputClick,
    inputValue,
    multiple,
    selectionLimit,
    id,
    collapse,
    ...rest
  } = props;
  const theme = useInterstateTheme();
  const style = resolveStyle(theme, maxHeight, size);
  const _channelId = useRef(nanoid());
  const [_fitWithinLimit, setFitWithinLimit] = useState(selectionLimit ? selectionLimit : selectedOptions.length);

  /**
   * Builds the option chips component.
   * The selectionLimit property is used if it has a value.  Otherwise, the fitWithinLimit is used.
   * @param {Array} selectedOptions - The array of selected options.
   * @param {Function} onChipClick - The function to be called when a chip is clicked.
   * @param {boolean} disabled - A flag indicating if the option chips are disabled.
   * @param {number} selectionLimit - The maximum number of options that can be selected. Defaults to `_fitWithinLimit`.
   * @param {boolean} collapse - A flag indicating if the option chips should collapse when all options are selected.
   *
   * @returns {Component} - The option chips component.
   */
  const optionChips = buildOptionChips(selectedOptions, onChipClick, disabled, selectionLimit || _fitWithinLimit, collapse);
  useEffect(() => {
    const broadcastChannel = new BroadcastChannel(_channelId.current);

    // listen for changes from FitWithinWidth
    // this informs us about how many children can fit
    // if there is an overflow then the overflow rendering can be done
    broadcastChannel.onmessage = event => {
      if (event.data.fittingChildrenCount) {
        setFitWithinLimit(event.data.fittingChildrenCount);
      }
    };
    return () => broadcastChannel.close();
  }, []);
  return /*#__PURE__*/_jsx(_Fragment, {
    children: /*#__PURE__*/_jsxs(Box, {
      sx: style,
      className: 'ids-combo-box-multi-select-input',
      children: [optionChips, /*#__PURE__*/_jsx(ComboBoxInput, {
        placeholder: placeholder,
        size: size,
        disabled: disabled,
        ref: ref,
        onFocus: onInputFocus,
        onInputChange: onInputChange,
        onInputClick: onInputClick,
        value: inputValue,
        multiple: multiple,
        width: selectedOptions.length === 0 ? '100%' : 'auto',
        "data-testid": rest['data-testid'],
        id: id
      }), /*#__PURE__*/_jsx(FitWithinWidth, {
        gap: 10,
        channelId: _channelId.current,
        children: buildOptionChips(selectedOptions, onChipClick, disabled, selectedOptions.length, false)
      }, nanoid())]
    })
  });
});
ComboBoxMultiSelect.displayName = 'ComboBoxMultiSelect';